import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import { supportAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
    Bug, 
    Lightbulb, 
    ArrowLeft, 
    Upload, 
    Check, 
    Clock, 
    AlertTriangle,
    CheckCircle,
    XCircle,
    Loader2,
    FileText,
    Send
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Composants de statut
const STATUS_CONFIG = {
    a_traiter: { label: 'À traiter', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-800', icon: Loader2 },
    resolu: { label: 'Résolu', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    refuse: { label: 'Refusé', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const URGENCY_CONFIG = {
    bloquant: { label: 'Bloquant', color: 'bg-red-500 text-white' },
    genant: { label: 'Gênant', color: 'bg-orange-500 text-white' },
    mineur: { label: 'Mineur', color: 'bg-slate-500 text-white' },
};

// Composant principal
export default function SupportPage() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [view, setView] = useState('choice'); // choice, bug, amelioration, my-tickets, admin
    const [loading, setLoading] = useState(false);
    const [modules, setModules] = useState([]);
    const [pipelines, setPipelines] = useState([]);
    // Ticket ouvert automatiquement (via deep-link Uatu ?ticket=<id>)
    const [expandedTicketId, setExpandedTicketId] = useState(null);
    const [myTickets, setMyTickets] = useState([]);
    const [adminTickets, setAdminTickets] = useState([]);
    const [adminStats, setAdminStats] = useState(null);
    const [successTicket, setSuccessTicket] = useState(null);
    
    // Formulaire Bug
    const [bugForm, setBugForm] = useState({
        pipeline_concerne: '',
        dossier_concerne: '',
        description: '',
        etapes_reproduction: '',
        urgence: 'genant',
    });
    const [bugAttachment, setBugAttachment] = useState(null);
    
    // Formulaire Amélioration
    const [amelioForm, setAmelioForm] = useState({
        module_concerne: '',
        description: '',
        probleme_resolu: '',
        benefice_attendu: '',
        exemple_concret: '',
    });
    const [amelioAttachment, setAmelioAttachment] = useState(null);

    // Charger les données initiales
    useEffect(() => {
        loadInitialData();
    }, []);

    // Deep-link Uatu : `?tab=my-tickets&ticket=<id>` — auto-ouvre l'onglet
    // « Mes tickets » et met en évidence + déplie le ticket ciblé.
    useEffect(() => {
        const tab = searchParams.get('tab');
        const ticketId = searchParams.get('ticket');
        if (tab === 'my-tickets') {
            setView('my-tickets');
            loadMyTickets();
        } else if (tab === 'admin') {
            setView('admin');
            loadAdminData();
        }
        if (ticketId) {
            setExpandedTicketId(ticketId);
            // Nettoie l'URL après ouverture pour éviter la persistance au refresh
            setTimeout(() => {
                const el = document.querySelector(`[data-testid="support-ticket-${ticketId}"]`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                try {
                    const next = new URLSearchParams(searchParams);
                    next.delete('ticket');
                    setSearchParams(next, { replace: true });
                } catch { /* noop */ }
            }, 500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get('tab'), searchParams.get('ticket')]);

    const loadInitialData = async () => {
        try {
            const [modulesRes, pipelinesRes] = await Promise.all([
                supportAPI.getModules(),
                supportAPI.getPipelines()
            ]);
            setModules(modulesRes.data);
            setPipelines(pipelinesRes.data);
        } catch (error) {
            console.error('Erreur chargement données:', error);
        }
    };

    const loadMyTickets = async () => {
        setLoading(true);
        try {
            const res = await supportAPI.getMyTickets();
            setMyTickets(res.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des tickets');
        } finally {
            setLoading(false);
        }
    };

    const loadAdminData = async () => {
        setLoading(true);
        try {
            const [ticketsRes, statsRes] = await Promise.all([
                supportAPI.adminGetAllTickets(),
                supportAPI.adminGetStats()
            ]);
            setAdminTickets(ticketsRes.data);
            setAdminStats(statsRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    // Handlers pour Bug Form - utilisant la forme fonctionnelle de setState
    const handleBugFieldChange = (field, value) => {
        setBugForm(prev => ({ ...prev, [field]: value }));
    };

    // Handlers pour Amelio Form - utilisant la forme fonctionnelle de setState
    const handleAmelioFieldChange = (field, value) => {
        setAmelioForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmitBug = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            let response;
            if (bugAttachment) {
                const formData = new FormData();
                formData.append('attachment', bugAttachment);
                Object.keys(bugForm).forEach(key => {
                    if (bugForm[key]) formData.append(key, bugForm[key]);
                });
                response = await supportAPI.createBugWithAttachment(formData);
            } else {
                response = await supportAPI.createBugTicket(bugForm);
            }
            
            setSuccessTicket(response.data);
            setView('success');
            setBugForm({
                pipeline_concerne: '',
                dossier_concerne: '',
                description: '',
                etapes_reproduction: '',
                urgence: 'genant',
            });
            setBugAttachment(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitAmelioration = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            let response;
            if (amelioAttachment) {
                const formData = new FormData();
                formData.append('attachment', amelioAttachment);
                Object.keys(amelioForm).forEach(key => {
                    if (amelioForm[key]) formData.append(key, amelioForm[key]);
                });
                response = await supportAPI.createAmeliorationWithAttachment(formData);
            } else {
                response = await supportAPI.createAmeliorationTicket(amelioForm);
            }
            
            setSuccessTicket(response.data);
            setView('success');
            setAmelioForm({
                module_concerne: '',
                description: '',
                probleme_resolu: '',
                benefice_attendu: '',
                exemple_concret: '',
            });
            setAmelioAttachment(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setLoading(false);
        }
    };

    const handleAdminStatusUpdate = async (ticketId, newStatus, comment = '') => {
        try {
            await supportAPI.adminUpdateStatus(ticketId, { status: newStatus, admin_comment: comment });
            toast.success('Statut mis à jour');
            loadAdminData();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    return (
        <DashboardLayout>
            <div className="p-6">
                {/* Vue Choix initial */}
                {view === 'choice' && (
                    <div className="max-w-3xl mx-auto">
                        <div className="text-center mb-8">
                            <h1 className="text-2xl font-bold text-slate-900 mb-2">Support & Amélioration</h1>
                            <p className="text-slate-600">Comment pouvons-nous vous aider ?</p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Option Bug */}
                            <Card 
                                className="cursor-pointer hover:border-red-500 hover:shadow-lg transition-all group"
                                onClick={() => setView('bug')}
                                data-testid="support-bug-option"
                            >
                                <CardContent className="p-8 text-center">
                                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-red-500 transition-colors">
                                        <Bug className="w-8 h-8 text-red-600 group-hover:text-white transition-colors" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Déclarer un bug</h3>
                                    <p className="text-sm text-slate-600">Signaler une dysfonctionnalité ou un problème technique</p>
                                </CardContent>
                            </Card>

                            {/* Option Amélioration */}
                            <Card 
                                className="cursor-pointer hover:border-amber-500 hover:shadow-lg transition-all group"
                                onClick={() => setView('amelioration')}
                                data-testid="support-amelioration-option"
                            >
                                <CardContent className="p-8 text-center">
                                    <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-500 transition-colors">
                                        <Lightbulb className="w-8 h-8 text-amber-600 group-hover:text-white transition-colors" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Proposer une amélioration</h3>
                                    <p className="text-sm text-slate-600">Suggérer une nouvelle fonctionnalité ou optimisation</p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Bouton Mes tickets */}
                        <div className="mt-8 text-center">
                            <Button 
                                variant="outline" 
                                onClick={() => { setView('my-tickets'); loadMyTickets(); }}
                                data-testid="support-my-tickets-btn"
                            >
                                <FileText className="w-4 h-4 mr-2" />
                                Voir mes demandes
                            </Button>
                        </div>

                        {/* Bouton Admin */}
                        {(user?.role === 'admin_full' || user?.is_admin === true) && (
                            <div className="mt-4 text-center">
                                <Button 
                                    variant="secondary" 
                                    onClick={() => { setView('admin'); loadAdminData(); }}
                                    data-testid="support-admin-btn"
                                >
                                    Gestion des tickets (Admin)
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* Vue Formulaire Bug */}
                {view === 'bug' && (
                    <div className="max-w-2xl mx-auto">
                        <Button 
                            variant="ghost" 
                            onClick={() => setView('choice')} 
                            className="mb-4"
                            data-testid="support-back-btn"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Retour
                        </Button>

                        <Card>
                            <CardHeader>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                        <Bug className="w-5 h-5 text-red-600" />
                                    </div>
                                    <div>
                                        <CardTitle>Déclarer un bug</CardTitle>
                                        <CardDescription>Décrivez le problème rencontré</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSubmitBug} className="space-y-6">
                                    {/* Info auto-remplies */}
                                    <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Utilisateur</span>
                                            <span className="font-medium">{user?.first_name} {user?.last_name}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Rôle</span>
                                            <span className="font-medium capitalize">{user?.role}</span>
                                        </div>
                                    </div>

                                    {/* Pipeline concerné */}
                                    <div className="space-y-2">
                                        <Label>Pipeline concerné (si applicable)</Label>
                                        <Select 
                                            value={bugForm.pipeline_concerne} 
                                            onValueChange={(v) => handleBugFieldChange('pipeline_concerne', v)}
                                        >
                                            <SelectTrigger data-testid="bug-pipeline-select">
                                                <SelectValue placeholder="Sélectionnez un pipeline" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {pipelines.map(p => (
                                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Dossier concerné */}
                                    <div className="space-y-2">
                                        <Label>Dossier concerné (si applicable)</Label>
                                        <Input 
                                            placeholder="Nom ou ID du dossier"
                                            value={bugForm.dossier_concerne}
                                            onChange={(e) => handleBugFieldChange('dossier_concerne', e.target.value)}
                                            data-testid="bug-dossier-input"
                                        />
                                    </div>

                                    {/* Description */}
                                    <div className="space-y-2">
                                        <Label>Description détaillée du problème *</Label>
                                        <Textarea 
                                            placeholder="Décrivez le problème rencontré..."
                                            rows={4}
                                            required
                                            value={bugForm.description}
                                            onChange={(e) => handleBugFieldChange('description', e.target.value)}
                                            data-testid="bug-description-textarea"
                                        />
                                    </div>

                                    {/* Étapes reproduction */}
                                    <div className="space-y-2">
                                        <Label>Étapes pour reproduire le bug *</Label>
                                        <Textarea 
                                            placeholder={"1. Aller sur...\n2. Cliquer sur...\n3. Le bug apparaît..."}
                                            rows={4}
                                            required
                                            value={bugForm.etapes_reproduction}
                                            onChange={(e) => handleBugFieldChange('etapes_reproduction', e.target.value)}
                                            data-testid="bug-steps-textarea"
                                        />
                                    </div>

                                    {/* Urgence */}
                                    <div className="space-y-2">
                                        <Label>Niveau d'urgence *</Label>
                                        <Select 
                                            value={bugForm.urgence} 
                                            onValueChange={(v) => handleBugFieldChange('urgence', v)}
                                            required
                                        >
                                            <SelectTrigger data-testid="bug-urgency-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="bloquant">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2 h-2 rounded-full bg-red-500" />
                                                        Bloquant
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="genant">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2 h-2 rounded-full bg-orange-500" />
                                                        Gênant
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="mineur">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2 h-2 rounded-full bg-slate-500" />
                                                        Mineur
                                                    </div>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Pièce jointe */}
                                    <div className="space-y-2">
                                        <Label>Capture d'écran / Fichier joint (optionnel)</Label>
                                        <div className="border-2 border-dashed rounded-lg p-4 text-center">
                                            <input 
                                                type="file" 
                                                id="bug-attachment"
                                                className="hidden"
                                                accept="image/*,.pdf,.doc,.docx"
                                                onChange={(e) => setBugAttachment(e.target.files[0])}
                                                data-testid="bug-attachment-input"
                                            />
                                            <label htmlFor="bug-attachment" className="cursor-pointer">
                                                <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                                                <p className="text-sm text-slate-600">
                                                    {bugAttachment ? bugAttachment.name : 'Cliquez pour ajouter un fichier (max 25 MB)'}
                                                </p>
                                            </label>
                                        </div>
                                    </div>

                                    <Button 
                                        type="submit" 
                                        className="w-full" 
                                        disabled={loading}
                                        data-testid="bug-submit-btn"
                                    >
                                        {loading ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Send className="w-4 h-4 mr-2" />
                                        )}
                                        Envoyer le signalement
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Vue Formulaire Amélioration */}
                {view === 'amelioration' && (
                    <div className="max-w-2xl mx-auto">
                        <Button 
                            variant="ghost" 
                            onClick={() => setView('choice')} 
                            className="mb-4"
                            data-testid="support-back-btn"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Retour
                        </Button>

                        <Card>
                            <CardHeader>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                        <Lightbulb className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <CardTitle>Proposer une amélioration</CardTitle>
                                        <CardDescription>Partagez votre idée avec nous</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSubmitAmelioration} className="space-y-6">
                                    {/* Info auto-remplies */}
                                    <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Utilisateur</span>
                                            <span className="font-medium">{user?.first_name} {user?.last_name}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Rôle</span>
                                            <span className="font-medium capitalize">{user?.role}</span>
                                        </div>
                                    </div>

                                    {/* Module concerné */}
                                    <div className="space-y-2">
                                        <Label>Module concerné *</Label>
                                        <Select 
                                            value={amelioForm.module_concerne} 
                                            onValueChange={(v) => handleAmelioFieldChange('module_concerne', v)}
                                            required
                                        >
                                            <SelectTrigger data-testid="amelio-module-select">
                                                <SelectValue placeholder="Sélectionnez un module" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {modules.map(m => (
                                                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Description */}
                                    <div className="space-y-2">
                                        <Label>Description de la proposition *</Label>
                                        <Textarea 
                                            placeholder="Décrivez votre idée d'amélioration..."
                                            rows={4}
                                            required
                                            value={amelioForm.description}
                                            onChange={(e) => handleAmelioFieldChange('description', e.target.value)}
                                            data-testid="amelio-description-textarea"
                                        />
                                    </div>

                                    {/* Problème résolu */}
                                    <div className="space-y-2">
                                        <Label>Problème que cela résout *</Label>
                                        <Textarea 
                                            placeholder="Quel problème cette amélioration résoudrait-elle ?"
                                            rows={3}
                                            required
                                            value={amelioForm.probleme_resolu}
                                            onChange={(e) => handleAmelioFieldChange('probleme_resolu', e.target.value)}
                                            data-testid="amelio-problem-textarea"
                                        />
                                    </div>

                                    {/* Bénéfice attendu */}
                                    <div className="space-y-2">
                                        <Label>Bénéfice attendu *</Label>
                                        <Textarea 
                                            placeholder="Quels seraient les avantages de cette amélioration ?"
                                            rows={3}
                                            required
                                            value={amelioForm.benefice_attendu}
                                            onChange={(e) => handleAmelioFieldChange('benefice_attendu', e.target.value)}
                                            data-testid="amelio-benefit-textarea"
                                        />
                                    </div>

                                    {/* Exemple concret */}
                                    <div className="space-y-2">
                                        <Label>Exemple concret (optionnel)</Label>
                                        <Textarea 
                                            placeholder="Un exemple concret d'utilisation..."
                                            rows={2}
                                            value={amelioForm.exemple_concret}
                                            onChange={(e) => handleAmelioFieldChange('exemple_concret', e.target.value)}
                                            data-testid="amelio-example-textarea"
                                        />
                                    </div>

                                    {/* Pièce jointe */}
                                    <div className="space-y-2">
                                        <Label>Fichier joint (optionnel)</Label>
                                        <div className="border-2 border-dashed rounded-lg p-4 text-center">
                                            <input 
                                                type="file" 
                                                id="amelio-attachment"
                                                className="hidden"
                                                accept="image/*,.pdf,.doc,.docx"
                                                onChange={(e) => setAmelioAttachment(e.target.files[0])}
                                                data-testid="amelio-attachment-input"
                                            />
                                            <label htmlFor="amelio-attachment" className="cursor-pointer">
                                                <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                                                <p className="text-sm text-slate-600">
                                                    {amelioAttachment ? amelioAttachment.name : 'Cliquez pour ajouter un fichier (max 25 MB)'}
                                                </p>
                                            </label>
                                        </div>
                                    </div>

                                    <Button 
                                        type="submit" 
                                        className="w-full" 
                                        disabled={loading}
                                        data-testid="amelio-submit-btn"
                                    >
                                        {loading ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Send className="w-4 h-4 mr-2" />
                                        )}
                                        Envoyer la proposition
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Vue Succès */}
                {view === 'success' && (
                    <div className="max-w-md mx-auto text-center">
                        <Card className="border-green-200 bg-green-50">
                            <CardContent className="pt-8 pb-8">
                                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                                    <Check className="w-8 h-8 text-green-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-green-900 mb-2">
                                    Demande envoyée !
                                </h2>
                                <p className="text-green-700 mb-4">
                                    Votre demande a bien été transmise à l'équipe technique.
                                </p>
                                {successTicket?.ticket_number && (
                                    <div className="bg-white rounded-lg p-3 mb-4">
                                        <p className="text-sm text-slate-600">Numéro de ticket</p>
                                        <p className="text-lg font-mono font-bold text-slate-900">
                                            {successTicket.ticket_number}
                                        </p>
                                    </div>
                                )}
                                <Button onClick={() => { setView('choice'); setSuccessTicket(null); }}>
                                    Retour à l'accueil
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Vue Mes Tickets */}
                {view === 'my-tickets' && (
                    <div className="max-w-4xl mx-auto">
                        <Button 
                            variant="ghost" 
                            onClick={() => setView('choice')} 
                            className="mb-4"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Retour
                        </Button>

                        <Card>
                            <CardHeader>
                                <CardTitle>Mes demandes</CardTitle>
                                <CardDescription>Historique de vos signalements et propositions</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                                    </div>
                                ) : myTickets.length === 0 ? (
                                    <div className="text-center py-8 text-slate-500">
                                        Aucune demande pour le moment
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {myTickets.map(ticket => {
                                            const isExpanded = expandedTicketId === ticket.id;
                                            return (
                                            <div 
                                                key={ticket.id}
                                                data-testid={`support-ticket-${ticket.id}`}
                                                onClick={() => setExpandedTicketId(isExpanded ? null : ticket.id)}
                                                className={cn(
                                                    'border rounded-lg p-4 transition-all cursor-pointer',
                                                    isExpanded
                                                        ? 'border-violet-400 bg-violet-50/50 ring-2 ring-violet-300 shadow-md'
                                                        : 'hover:border-slate-300 hover:bg-slate-50'
                                                )}
                                            >
                                                <div className="flex items-start justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        {ticket.type === 'bug' ? (
                                                            <Bug className="w-4 h-4 text-red-500" />
                                                        ) : (
                                                            <Lightbulb className="w-4 h-4 text-amber-500" />
                                                        )}
                                                        <span className="font-mono text-sm text-slate-600">
                                                            {ticket.ticket_number}
                                                        </span>
                                                        {ticket.created_via === 'uatu' && (
                                                            <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-700 bg-violet-50">
                                                                via Uatu
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <Badge className={STATUS_CONFIG[ticket.status]?.color}>
                                                        {STATUS_CONFIG[ticket.status]?.label}
                                                    </Badge>
                                                </div>
                                                <p className={cn('text-sm text-slate-900 mb-2', !isExpanded && 'line-clamp-2')}>
                                                    {ticket.description}
                                                </p>
                                                {isExpanded && (
                                                    <div className="mt-3 pt-3 border-t border-violet-200 space-y-2 text-sm">
                                                        {ticket.type === 'bug' ? (
                                                            <>
                                                                {ticket.pipeline_concerne && (
                                                                    <div><span className="font-semibold text-slate-700">Pipeline :</span> <span className="text-slate-600">{ticket.pipeline_concerne}</span></div>
                                                                )}
                                                                {ticket.dossier_concerne && (
                                                                    <div><span className="font-semibold text-slate-700">Dossier :</span> <span className="text-slate-600">{ticket.dossier_concerne}</span></div>
                                                                )}
                                                                {ticket.etapes_reproduction && (
                                                                    <div><span className="font-semibold text-slate-700">Étapes de reproduction :</span><br /><span className="text-slate-600 whitespace-pre-wrap">{ticket.etapes_reproduction}</span></div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                {ticket.module_concerne && (
                                                                    <div><span className="font-semibold text-slate-700">Module :</span> <span className="text-slate-600">{ticket.module_concerne}</span></div>
                                                                )}
                                                                {ticket.probleme_resolu && (
                                                                    <div><span className="font-semibold text-slate-700">Problème résolu :</span><br /><span className="text-slate-600 whitespace-pre-wrap">{ticket.probleme_resolu}</span></div>
                                                                )}
                                                                {ticket.benefice_attendu && (
                                                                    <div><span className="font-semibold text-slate-700">Bénéfice attendu :</span><br /><span className="text-slate-600 whitespace-pre-wrap">{ticket.benefice_attendu}</span></div>
                                                                )}
                                                                {ticket.exemple_concret && (
                                                                    <div><span className="font-semibold text-slate-700">Exemple :</span><br /><span className="text-slate-600 whitespace-pre-wrap">{ticket.exemple_concret}</span></div>
                                                                )}
                                                            </>
                                                        )}
                                                        {ticket.admin_comment && (
                                                            <div className="rounded-md bg-blue-50 border border-blue-200 p-2 mt-2">
                                                                <span className="font-semibold text-blue-800">Réponse équipe :</span><br /><span className="text-blue-900 whitespace-pre-wrap">{ticket.admin_comment}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-4 text-xs text-slate-500 mt-2">
                                                    <span>{new Date(ticket.created_at).toLocaleDateString('fr-FR')}</span>
                                                    {ticket.urgence && (
                                                        <Badge variant="outline" className={cn('text-xs', URGENCY_CONFIG[ticket.urgence]?.color)}>
                                                            {URGENCY_CONFIG[ticket.urgence]?.label}
                                                        </Badge>
                                                    )}
                                                    <span className="ml-auto text-slate-400 italic">
                                                        {isExpanded ? 'Clique pour replier' : 'Clique pour voir le détail'}
                                                    </span>
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Vue Admin */}
                {view === 'admin' && (
                    <div className="max-w-6xl mx-auto">
                        <Button 
                            variant="ghost" 
                            onClick={() => setView('choice')} 
                            className="mb-4"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Retour
                        </Button>

                        {/* Stats */}
                        {adminStats && (
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                                <Card>
                                    <CardContent className="pt-4 pb-4 text-center">
                                        <p className="text-2xl font-bold">{adminStats.total}</p>
                                        <p className="text-sm text-slate-500">Total</p>
                                    </CardContent>
                                </Card>
                                <Card className="border-yellow-200">
                                    <CardContent className="pt-4 pb-4 text-center">
                                        <p className="text-2xl font-bold text-yellow-600">{adminStats.by_status?.a_traiter || 0}</p>
                                        <p className="text-sm text-slate-500">À traiter</p>
                                    </CardContent>
                                </Card>
                                <Card className="border-blue-200">
                                    <CardContent className="pt-4 pb-4 text-center">
                                        <p className="text-2xl font-bold text-blue-600">{adminStats.by_status?.en_cours || 0}</p>
                                        <p className="text-sm text-slate-500">En cours</p>
                                    </CardContent>
                                </Card>
                                <Card className="border-green-200">
                                    <CardContent className="pt-4 pb-4 text-center">
                                        <p className="text-2xl font-bold text-green-600">{adminStats.by_status?.resolu || 0}</p>
                                        <p className="text-sm text-slate-500">Résolus</p>
                                    </CardContent>
                                </Card>
                                <Card className="border-red-200">
                                    <CardContent className="pt-4 pb-4 text-center">
                                        <p className="text-2xl font-bold text-red-600">{adminStats.by_status?.refuse || 0}</p>
                                        <p className="text-sm text-slate-500">Refusés</p>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        <Card>
                            <CardHeader>
                                <CardTitle>Gestion des tickets</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Tabs defaultValue="all">
                                    <TabsList className="mb-4">
                                        <TabsTrigger value="all">Tous</TabsTrigger>
                                        <TabsTrigger value="a_traiter">À traiter</TabsTrigger>
                                        <TabsTrigger value="en_cours">En cours</TabsTrigger>
                                        <TabsTrigger value="resolu">Résolus</TabsTrigger>
                                    </TabsList>

                                    {['all', 'a_traiter', 'en_cours', 'resolu'].map(tab => (
                                        <TabsContent key={tab} value={tab}>
                                            {loading ? (
                                                <div className="flex items-center justify-center py-8">
                                                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {adminTickets
                                                        .filter(t => tab === 'all' || t.status === tab)
                                                        .map(ticket => (
                                                            <div 
                                                                key={ticket.id} 
                                                                className="border rounded-lg p-4"
                                                            >
                                                                <div className="flex items-start justify-between mb-3">
                                                                    <div>
                                                                        <div className="flex items-center gap-2 mb-1">
                                                                            {ticket.type === 'bug' ? (
                                                                                <Bug className="w-4 h-4 text-red-500" />
                                                                            ) : (
                                                                                <Lightbulb className="w-4 h-4 text-amber-500" />
                                                                            )}
                                                                            <span className="font-mono text-sm font-medium">
                                                                                {ticket.ticket_number}
                                                                            </span>
                                                                            {ticket.urgence && (
                                                                                <Badge className={URGENCY_CONFIG[ticket.urgence]?.color}>
                                                                                    {URGENCY_CONFIG[ticket.urgence]?.label}
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                        <p className="text-sm text-slate-600">
                                                                            {ticket.user_name} ({ticket.user_role})
                                                                        </p>
                                                                    </div>
                                                                    <Badge className={STATUS_CONFIG[ticket.status]?.color}>
                                                                        {STATUS_CONFIG[ticket.status]?.label}
                                                                    </Badge>
                                                                </div>
                                                                
                                                                <p className="text-sm text-slate-900 mb-3">
                                                                    {ticket.description}
                                                                </p>

                                                                <div className="flex items-center gap-2">
                                                                    <Select 
                                                                        value={ticket.status}
                                                                        onValueChange={(v) => handleAdminStatusUpdate(ticket.id, v)}
                                                                    >
                                                                        <SelectTrigger className="w-40">
                                                                            <SelectValue />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            <SelectItem value="a_traiter">À traiter</SelectItem>
                                                                            <SelectItem value="en_cours">En cours</SelectItem>
                                                                            <SelectItem value="resolu">Résolu</SelectItem>
                                                                            <SelectItem value="refuse">Refusé</SelectItem>
                                                                        </SelectContent>
                                                                    </Select>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    {adminTickets.filter(t => tab === 'all' || t.status === tab).length === 0 && (
                                                        <div className="text-center py-8 text-slate-500">
                                                            Aucun ticket dans cette catégorie
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </TabsContent>
                                    ))}
                                </Tabs>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
